<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../js/api.js';
  import { auth } from '../js/auth.js';
  import { toast } from '../js/toast.js';
  import { push } from 'svelte-spa-router';
  import {
    AppPageHeader,
    AppEmptyState,
    AppLoadingState,
    AppErrorState,
    StatusBadge,
  } from './app';

  let invoices: any[] = [];
  let state: 'loading' | 'ready' | 'error' = 'loading';
  let downloadingUuid: string | null = null;

  async function loadInvoices() {
    state = 'loading';
    try {
      const response = await api.getUserInvoices($auth.user.uuid) as { success: boolean; data?: { invoices?: any[] }; message?: string };
      if (response.success && response.data) {
        invoices = response.data.invoices || [];
        state = 'ready';
      } else {
        toast.showNotification(false, response.message || 'Failed to load invoices');
        state = 'error';
      }
    } catch (error) {
      console.error(error);
      state = 'error';
    }
  }

  function formatPrice(price: number): string {
    return new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'USD' }).format(price);
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(navigator.language, { dateStyle: 'medium' });
  }

  function statusTone(status: string): 'info' | 'success' | 'warn' | 'danger' | 'neutral' {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'danger';
      case 'sent': return 'info';
      default: return 'neutral';
    }
  }

  function viewInvoice(invoiceUuid: string, e?: Event) {
    if (e) e.stopPropagation();
    if (typeof window !== 'undefined') {
      window.location.hash = '#/invoices/' + invoiceUuid;
    } else {
      push(`/invoices/${invoiceUuid}`);
    }
  }

  async function downloadInvoice(invoice: any, e: Event) {
    e.stopPropagation();
    if (downloadingUuid) return;
    downloadingUuid = invoice.uuid;
    try {
      await api.downloadInvoicePdf(invoice.uuid, invoice.invoice_number);
      toast.showNotification(true, 'Invoice downloaded');
    } catch {
      toast.showNotification(false, 'Failed to download invoice');
    } finally {
      downloadingUuid = null;
    }
  }

  onMount(loadInvoices);
</script>

<div class="invoice-page">
  <AppPageHeader
    title="Invoices"
    subtitle="View your payment history."
    showAction={false}
  />

  {#if state === 'loading'}
    <AppLoadingState label="Loading invoices" />
  {:else if state === 'error'}
    <AppErrorState on:retry={loadInvoices} />
  {:else if invoices.length === 0}
    <AppEmptyState
      icon="file outline"
      title="No invoices yet"
      description="Your invoices will appear here once you have payment activity."
    />
  {:else}
    <div class="invoices-card">
      <div class="table-wrapper">
        <table class="invoices-table" role="grid">
          <thead>
            <tr>
              <th scope="col">Invoice</th>
              <th scope="col">Date</th>
              <th scope="col">Due date</th>
              <th scope="col">Amount</th>
              <th scope="col">Status</th>
              <th scope="col"><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {#each invoices as invoice (invoice.uuid)}
              <tr
                class="invoice-row"
                role="button"
                tabindex="0"
                on:click={() => viewInvoice(invoice.uuid)}
                on:keydown={(e) => e.key === 'Enter' && viewInvoice(invoice.uuid)}
              >
                <td class="invoice-number-cell"><span class="invoice-number">{invoice.invoice_number}</span></td>
                <td>{formatDate(invoice.invoice_date)}</td>
                <td>{formatDate(invoice.due_date)}</td>
                <td class="amount-cell">{formatPrice(invoice.total)}</td>
                <td>
                  <StatusBadge
                    label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    tone={statusTone(invoice.status)}
                  />
                </td>
                <td class="actions-cell">
                  <button type="button" class="btn btn-secondary" on:click={(e) => viewInvoice(invoice.uuid, e)}>View</button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    disabled={downloadingUuid === invoice.uuid}
                    on:click={(e) => downloadInvoice(invoice, e)}
                  >
                    {#if downloadingUuid === invoice.uuid}
                      <span class="btn-spinner" aria-hidden="true"></span>
                      <span>Downloading…</span>
                    {:else}
                      <i class="download icon" aria-hidden="true"></i>
                      <span>Download</span>
                    {/if}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

<style>
  .invoice-page { padding: var(--space-5) var(--space-6); }

  .invoices-card {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-card);
    overflow: hidden;
  }

  .table-wrapper { overflow-x: auto; }

  .invoices-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-base);
  }

  .invoices-table th {
    text-align: left;
    padding: var(--space-4) var(--space-5);
    background: var(--surface-hover);
    font-weight: 500;
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    border-bottom: 1px solid var(--border-color);
  }

  .invoices-table td {
    padding: var(--space-4) var(--space-5);
    border-bottom: 1px solid var(--greyish);
    color: var(--grey);
    vertical-align: middle;
  }

  .invoice-row { cursor: pointer; transition: background-color .15s; }
  .invoice-row:hover { background-color: var(--surface-hover); }
  .invoice-row:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--primary); }
  .invoice-row:last-child td { border-bottom: 0; }

  .invoice-number-cell { font-weight: 500; color: var(--grey); }
  .invoice-number,
  .amount-cell { font-variant-numeric: tabular-nums; }
  .amount-cell { font-weight: 500; }

  .actions-cell { white-space: nowrap; }
  .actions-cell .btn + .btn { margin-left: var(--space-2); }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-2) var(--space-3);
    border: 0;
    border-radius: var(--radius-input);
    font-size: var(--text-sm);
    font-weight: 500;
    cursor: pointer;
    transition: background-color .15s, filter .15s;
  }

  .btn:disabled { opacity: .7; cursor: not-allowed; }
  .btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

  .btn-secondary { background: var(--greyish); color: var(--grey); border: 1px solid var(--border-color); }
  .btn-secondary:hover:not(:disabled) { background: var(--surface-hover); border-color: var(--border-strong); }

  .btn-primary { background: var(--primary); color: var(--white); box-shadow: var(--shadow-cta); }
  .btn-primary:hover:not(:disabled) { filter: brightness(.95); }

  .btn-spinner {
    width: 1rem; height: 1rem;
    border: 2px solid rgba(255,255,255,.4);
    border-top-color: var(--white);
    border-radius: 50%;
    animation: spin .8s linear infinite;
    flex-shrink: 0;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .sr-only {
    position: absolute; width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0,0,0,0);
    white-space: nowrap; border: 0;
  }

  @media (max-width: 768px) {
    .invoice-page { padding: var(--space-4); }
    .invoices-table th,
    .invoices-table td { padding: var(--space-3) var(--space-4); font-size: var(--text-sm); }
    .actions-cell { display: flex; flex-wrap: wrap; gap: var(--space-2); }
    .actions-cell .btn + .btn { margin-left: 0; }
  }
</style>
